import { createFileRoute } from "@tanstack/react-router";
import { type ColumnDef } from "@tanstack/react-table";
import { listNewsletterSubscribers, exportNewsletterSubscribersCsv, getNewsletterStats } from "@/lib/api/newsletter.functions";
import { DataTable } from "@/components/admin/DataTable";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { AdminPageShell } from "@/components/admin/AdminPageShell";
import { NewsletterAdminNav } from "@/components/admin/NewsletterAdminNav";
import { StatusBadge } from "@/components/admin/StatusBadge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

export const Route = createFileRoute("/admin/newsletter/subscribers/")({
  loader: async () => {
    const [subscribers, stats] = await Promise.all([listNewsletterSubscribers(), getNewsletterStats()]);
    return { subscribers, stats };
  },
  component: SubscribersPage,
});

type Row = Awaited<ReturnType<typeof listNewsletterSubscribers>>[number];

const columns: ColumnDef<Row>[] = [
  { accessorKey: "email", header: "Email" },
  {
    accessorKey: "status",
    header: "Status",
    cell: ({ row }) => <StatusBadge status={row.original.status} />,
  },
  { accessorKey: "source", header: "Source" },
  {
    accessorKey: "subscribedAt",
    header: "Subscribed",
    cell: ({ row }) => (row.original.subscribedAt ? new Date(row.original.subscribedAt).toLocaleString() : ""),
  },
  {
    accessorKey: "unsubscribedAt",
    header: "Unsubscribed",
    cell: ({ row }) => (row.original.unsubscribedAt ? new Date(row.original.unsubscribedAt).toLocaleString() : ""),
  },
  {
    accessorKey: "optOutReason",
    header: "Opt-out reason",
    cell: ({ row }) => row.original.optOutReason ?? "—",
  },
];

function SubscribersPage() {
  const { subscribers, stats } = Route.useLoaderData();

  async function handleExport() {
    const { csv } = await exportNewsletterSubscribersCsv();
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "newsletter-subscribers.csv";
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <AdminPageShell>
      <AdminPageHeader title="Newsletter — Subscribers" />
      <NewsletterAdminNav />
      <div className="grid gap-4 sm:grid-cols-2 lg:max-w-xl">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Subscribed</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold">{stats.subscribed}</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Unsubscribed</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-3xl font-bold">{stats.unsubscribed}</p>
          </CardContent>
        </Card>
      </div>
      <div>
        <h2 className="mb-2 text-lg font-semibold">Newsletter sign-ups</h2>
        <p className="mb-4 text-sm text-muted-foreground">Emails collected from the insights page newsletter form.</p>
        <DataTable tableId="newsletter-subscribers" columns={columns} data={subscribers} onExportCsv={handleExport} />
      </div>
    </AdminPageShell>
  );
}
